import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChevronLeft, ChevronRight, History, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

interface TradingHistoryRow {
  index: number;
  type?: string;
  timestamp: string;
  signal: string;
  price: number;
  position_size: number;
  position_size_raw: string;
  position_value?: number;
  pnl: number;
  normalized_pnl: number;
  net_pnl_percent: number;
  runup_usd?: number;
  runup_percent?: number;
  drawdown_usd?: number;
  drawdown_percent?: number;
  cumulative_pnl: number;
  cumulative_percent: number;
  isSkipped?: boolean;
  skipReason?: string;
  filename?: string;
}

interface TradingHistoryTableProps {
  data: TradingHistoryRow[];
}

export const TradingHistoryTable: React.FC<TradingHistoryTableProps> = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(50);
  
  const usedTrades = data.filter(trade => !trade.isSkipped);
  const skippedTrades = data.filter(trade => trade.isSkipped);
  const allSkipped = data.length > 0 && skippedTrades.length === data.length;
  
  const totalPages = Math.ceil(data.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = data.slice(startIndex, startIndex + rowsPerPage);

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History size={20} />
            Trading History ({data.length} trades - {usedTrades.length} used, {skippedTrades.length} skipped)
          </CardTitle>
          {skippedTrades.length > 0 && (
            <Badge variant="secondary" className="bg-orange-500/20 text-orange-700 border-orange-500/30">
              Skipped: {skippedTrades.length} trades
            </Badge>
          )}
        </div>
        {allSkipped && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              All trades have been skipped due to loss streak rules. Consider adjusting the loss streak settings.
            </AlertDescription>
          </Alert>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Pagination Controls */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Showing {startIndex + 1}-{Math.min(startIndex + rowsPerPage, data.length)} of {data.length} trades
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-32">File</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Date/Time</TableHead>
                  <TableHead>Signal</TableHead>
                  <TableHead className="text-right">Price USD</TableHead>
                  <TableHead className="text-right">Position size (qty)</TableHead>
                  <TableHead className="text-right">Position size (value)</TableHead>
                  <TableHead className="text-right">P&L USD</TableHead>
                  <TableHead className="text-right">Net P&L %</TableHead>
                  <TableHead className="text-right">Run-up USD</TableHead>
                  <TableHead className="text-right">Run-up %</TableHead>
                  <TableHead className="text-right">Drawdown USD</TableHead>
                  <TableHead className="text-right">Drawdown %</TableHead>
                  <TableHead className="text-right">Cumulative P&L USD</TableHead>
                  <TableHead className="text-right">Cumulative P&L %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedData.map((row) => (
                  <TableRow 
                    key={`${row.index}-${row.timestamp}`}
                    className={row.isSkipped ? "bg-muted/30 opacity-60" : ""}
                  >
                    <TableCell className="font-medium">
                      {row.index}
                      {row.isSkipped && (
                        <span className="ml-1 text-xs text-muted-foreground">(skipped)</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-32">
                      <div className="truncate" title={row.filename || 'Unknown'}>
                        {row.filename || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.type || '—'}
                      {row.isSkipped && row.skipReason && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {row.skipReason}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {formatDate(row.timestamp)}
                    </TableCell>
                    <TableCell>{row.signal || '—'}</TableCell>
                    <TableCell className="text-right font-mono">
                      {row.price > 0 ? formatCurrency(row.price) : '—'}
                    </TableCell>
                     <TableCell className="text-right font-mono">
                       {row.position_size_raw || row.position_size}
                     </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.position_value ? formatCurrency(row.position_value) : '—'}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${
                      row.normalized_pnl > 0 ? 'text-primary' : 
                      row.normalized_pnl < 0 ? 'text-destructive' : ''
                    }`}>
                      {formatCurrency(row.normalized_pnl)}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${
                      row.net_pnl_percent > 0 ? 'text-primary' : 
                      row.net_pnl_percent < 0 ? 'text-destructive' : ''
                    }`}>
                      {formatPercent(row.net_pnl_percent)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.runup_usd !== undefined ? formatCurrency(row.runup_usd) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.runup_percent !== undefined ? formatPercent(row.runup_percent) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.drawdown_usd !== undefined ? formatCurrency(row.drawdown_usd) : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {row.drawdown_percent !== undefined ? formatPercent(row.drawdown_percent) : '—'}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${
                      row.cumulative_pnl > 0 ? 'text-primary' : 
                      row.cumulative_pnl < 0 ? 'text-destructive' : ''
                    }`}>
                      {formatCurrency(row.cumulative_pnl)}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${
                      row.cumulative_percent > 0 ? 'text-primary' : 
                      row.cumulative_percent < 0 ? 'text-destructive' : ''
                    }`}>
                      {formatPercent(row.cumulative_percent)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Bottom Pagination */}
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
              Previous
            </Button>
            <span className="text-sm px-4">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};